import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { shopifyFetch, SEARCH_PRODUCTS, nodes } from "@/lib/shopify";
import { Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [results, setResults] = useState([]);
  const [state, setState] = useState("idle");

  useEffect(() => {
    setInput(q);
    if (!q.trim()) {
      setResults([]);
      setState("idle");
      return;
    }
    let cancelled = false;
    setState("loading");
    const t = setTimeout(() => {
      shopifyFetch(SEARCH_PRODUCTS, { query: q, first: 24 })
        .then((d) => {
          if (!cancelled) {
            setResults(nodes(d.products));
            setState("ready");
          }
        })
        .catch(() => !cancelled && setState("error"));
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  return (
    <main data-testid="search-page" className="min-h-screen bg-void pt-28 sm:pt-32">
      <Seo
        title="Search — Weird O'Clock"
        siteName="Weird O'Clock"
        description="Search the Weird O'Clock catalogue of surreal clocks and odd home decor."
      />
      <div className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
        <Eyebrow>Find your thing</Eyebrow>
        <h1 className="font-display text-4xl font-black tracking-tight text-ivory sm:text-5xl lg:text-6xl">
          Search the <span className="font-editorial italic text-amber">strange</span>
        </h1>

        <form onSubmit={submit} className="mt-10 flex max-w-2xl items-center gap-3" data-testid="search-form">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ivory/40" />
            <input
              data-testid="search-input"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setParams(e.target.value.trim() ? { q: e.target.value.trim() } : {});
              }}
              placeholder="melted clock, mushroom lamp, cursed vase…"
              className="w-full rounded-full border border-white/15 bg-obsidian py-4 pl-12 pr-5 text-base text-ivory placeholder:text-ivory/30 outline-none transition-colors focus:border-amber"
            />
          </div>
        </form>

        {state === "loading" && (
          <div className="flex items-center gap-3 py-20 text-ivory/40" data-testid="search-loading">
            <Loader2 className="h-5 w-5 animate-spin text-amber" />
            <span className="font-mono2 text-xs uppercase tracking-[0.25em]">Rummaging…</span>
          </div>
        )}
        {state === "error" && (
          <p className="mt-10 rounded-lg border border-amber/30 bg-amber/5 px-5 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-amber" data-testid="search-error">
            Connecting to the Weird O'Clock Shopify store — check back in a tick.
          </p>
        )}
        {state === "ready" && (
          <p className="mt-8 font-mono2 text-[10px] uppercase tracking-[0.3em] text-ivory/30" data-testid="search-result-count">
            {results.length} result{results.length === 1 ? "" : "s"} for "{q}"
          </p>
        )}
        {state === "ready" && results.length === 0 && (
          <p className="mt-16 font-editorial text-2xl italic text-ivory/60">
            Nothing that weird… yet. Try "clock" or "vase".
          </p>
        )}
        <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4">
          {results.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </main>
  );
}
